import { RosterSetupContext } from '../../../../TeamRoster/components/RosterSetup'
import { useTile } from '../../../context'
import { useContext } from 'react'
import { characterRoles } from 'app/frontend/types/custom/CharacterRole'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import { CharacterClassIcon } from 'app/frontend/base/components'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import capitalize from 'lodash/capitalize'
import { cn } from '@/lib/utils'
import { Link } from 'react-router-dom'
import Card from 'app/frontend/base/components/Card'

const ByRole = ({ className }: React.HTMLAttributes<HTMLDivElement>) => {
  const { tile } = useTile()
  const { mainsByRole, altsByRole, offSpecsByRole } =
    useContext(RosterSetupContext)

  return (
    <div
      className={cn(
        className,
        tile.position === 'main'
          ? 'grid grid-cols-2 md:grid-cols-4 gap-2'
          : 'space-y-2',
      )}
    >
      {characterRoles.map((role) => {
        const mains = mainsByRole[role] ?? []
        const alts = altsByRole[role] ?? []
        const offspecs = offSpecsByRole[role] ?? []
        const altAndOffspecCount = alts.length + offspecs.length

        const altAndOffspecList = [
          ...alts.map((alt) => ({
            ...alt.characterReference,
            type: 'alt' as const,
          })),
          ...offspecs.map((item) => ({
            ...item.member.characterReference,
            type: 'offspec' as const,
          })),
        ]

        return (
          <Card key={role} className={cn('space-y-2 py-2 px-3')}>
            <div className='flex items-center justify-between pb-2 mb-2 border-b'>
              <div className='flex items-center gap-2'>
                <CharacterRoleIcon role={role} size='large' />
                <span className='text-base font-medium'>
                  {capitalize(role)}
                </span>
              </div>
              <Tooltip>
                <TooltipTrigger>
                  <Badge
                    variant='outline'
                    className={'text-base cursor-pointer'}
                  >
                    {mains.length}
                    {altAndOffspecCount > 0 && (
                      <span className='ml-1 text-[0.65rem] text-muted-foreground'>
                        +{altAndOffspecCount}
                      </span>
                    )}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent
                  side={tile.position === 'main' ? 'bottom' : 'left'}
                >
                  <div className='font-medium border-b mb-1 flex items-center justify-between'>
                    Mains
                    <span className='text-muted-foreground text-xs'>
                      {mains.length}
                    </span>
                  </div>
                  <div className='space-y-1'>
                    {mains.map((main) => (
                      <Link
                        to={main.characterReference.path}
                        key={main.id}
                        className='flex items-center justify-between gap-2 text-xs'
                      >
                        <CharacterClassIcon
                          characterClass={
                            main.characterReference.characterClass
                          }
                          size='small'
                          style='round'
                        />
                        <span className='grow'>
                          {main.characterReference.name}
                        </span>
                      </Link>
                    ))}
                  </div>

                  {altAndOffspecCount > 0 && (
                    <>
                      <div className='font-medium border-b mt-2 mb-1 flex items-center justify-between'>
                        Other
                        <span className='text-muted-foreground text-xs'>
                          {altAndOffspecList.length}
                        </span>
                      </div>

                      <div className='space-y-1'>
                        {altAndOffspecList.map((item, index) => (
                          <Link
                            to={item.path}
                            key={`${item.name}-${index}`}
                            className='flex items-center justify-between gap-2 text-xs'
                          >
                            <CharacterClassIcon
                              characterClass={item.characterClass}
                              size='small'
                              style='round'
                            />
                            <span className='grow'>{item.name}</span>
                            <span className='text-muted-foreground'>
                              ({item.type})
                            </span>
                          </Link>
                        ))}
                      </div>
                    </>
                  )}
                </TooltipContent>
              </Tooltip>
            </div>

            <div className='flex flex-wrap gap-1.5'>
              {mains.map((main) => (
                <Tooltip key={main.id}>
                  <TooltipTrigger>
                    <Link to={main.characterReference.path}>
                      <CharacterClassIcon
                        characterClass={main.characterReference.characterClass}
                        style='round'
                      />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent>
                    {main.characterReference.name}
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          </Card>
        )
      })}
    </div>
  )
}

export default ByRole
